import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {delay, filter, firstValueFrom, from, map, Observable, of, repeat, switchMap, take, tap, throwError, timeout} from 'rxjs';
import {DeviceDetectorService} from 'ngx-device-detector';
import {NGXLogger} from 'ngx-logger';
import {
  ApiMode,
  CallbackPayload,
  ConfigService, ConnectMode, ConnectPayload, CspType,
  buildSupportRequestZendeskTicket,
  FormHealthSystemRequest,
  FormSupportRequest, GeocodeResponse,
  isNativeSdkMode,
  LighthouseSourceSearch,
  LighthouseSourceSearchResult,
  Organization,
  OrganizationConnection,
  RecordLocatorResponse,
  ResponseWrapper,
  SDKMode,
  SearchFilter,
  SystemConfig,
  waitForPostMessageOrgConnectionOrTimeout,
  waitForWebsocketOrgConnectionOrTimeout
} from '@shared-library';
import {v4 as uuidv4} from "uuid";
import {AuthService, FASTEN_AUTH_VAULT_COOKIE_NAME, VaultAuthHandoffPurpose} from "./auth.service";
import {CallbackAttempt, CallbackFlow, SentryContextService} from './sentry-context.service';

@Injectable({
  providedIn: 'root'
})

export class FastenService {
  constructor(
    private _httpClient: HttpClient,
    private deviceService: DeviceDetectorService,
    public configService: ConfigService,
    private logger: NGXLogger,
    private sentryContext: SentryContextService,
    private authService: AuthService,
  ) {

    //always watch for changes to the vault
    this.configService.systemConfigSubject.subscribe((systemConfig: SystemConfig) => {
      const isPlaceholderConfig = !systemConfig.publicId
        && systemConfig.searchOnly === undefined
        && systemConfig.tefcaMode === undefined
        && systemConfig.connectMode === undefined;
      if (isPlaceholderConfig) {
        return;
      }

      this.logger.info("System configuration changed:", systemConfig, this.configService.systemConfig$)
      if(systemConfig.org_id && !systemConfig.org){
        this.logger.info("attempt to download org information, and store in config")
        this.getOrgConfig(systemConfig.publicId).subscribe((org: Organization )=>{
          this.logger.debug("org:", org)
          this.configService.systemConfig = {org: org}
        })
      }
    });
  }

  public recordLocatorRegisterAndPollForStatus(): Observable<ResponseWrapper> {
// https://codewithstyle.info/rxjs-polling/
    return this._httpClient.get<ResponseWrapper>(`${environment.connect_api_endpoint_base}/bridge/record_locator`,
      {params: {"public_id": this.configService.systemConfig$.publicId }})
      .pipe(
        switchMap((registerResponse: ResponseWrapper) => {
          if (registerResponse.data.status == 'failed') {
            console.error("record locator registration failed", registerResponse.data)
            return throwError(() => new Error('Record locator registration failed'));
          } else if (registerResponse.data.status == 'success') {
            console.info("record locator already successful, no need to poll")
            return of(registerResponse); // If already successful, return the data immediately eg, {task_id: "XXX", status: "success"}
          } else {
            // status is pending - start polling
            console.log("start polling for record locator status")
            return this._httpClient.get<ResponseWrapper>(`${environment.connect_api_endpoint_base}/bridge/record_locator/${registerResponse.data.task_id}`,
              {params: {"public_id": this.configService.systemConfig$.publicId}})
              .pipe(
                repeat({delay: 3000}), //check every 3 seconds
                filter((statusResponse: ResponseWrapper) => statusResponse.data.status === "success" || statusResponse.data.status === "failed"), //only pass through successful or failed status
                take(1),
                timeout(300000) // Stop polling after 5 minutes
              )
          }
        })
      )
  }

  public recordLocatorResults(taskId: string): Observable<RecordLocatorResponse> {
    // let _this = this;
    return this._httpClient.get<ResponseWrapper>(`${environment.connect_api_endpoint_base}/bridge/record_locator/${taskId}/result`,
      {params: {"public_id": this.configService.systemConfig$.publicId }})
      .pipe(
        map((response: ResponseWrapper) => {
          // _this.logger.info("Record Locator Response", response)
          let rlsResponse = response.data as RecordLocatorResponse;
          //for safety purposes, make sure we always return an object with the two expected keys and empty objects (not null)
          if(!rlsResponse.discovered_patient_accounts){
            rlsResponse.discovered_patient_accounts = {}
          }
          if(!rlsResponse.pending_patient_accounts){
            rlsResponse.pending_patient_accounts = {}
          }
          return rlsResponse
        })
      );
  }

  public searchCatalog(apiMode: ApiMode, filter: SearchFilter): Observable<LighthouseSourceSearch> {
    if((typeof filter.searchAfter === 'string' || filter.searchAfter instanceof String) && (filter.searchAfter as string).length > 0){
      filter.searchAfter = (filter.searchAfter as string).split(',')
    } else {
      filter.searchAfter = []
    }
    const endpointUrl = new URL(`${environment.connect_api_endpoint_base}/bridge/catalog/search`);

    return this._httpClient.post<ResponseWrapper>(endpointUrl.toString(), filter, {params: {
        "public_id": this.configService.systemConfig$.publicId,
        'api_mode': apiMode
      }})
      .pipe(
        map((response: ResponseWrapper) => {
          this.logger.info("Metadata RESPONSE", response)
          return response.data as LighthouseSourceSearch
        })
      );
  }


  public searchCatalogBrand(apiMode: ApiMode, brandId: string): Observable<LighthouseSourceSearchResult> {

    const endpointUrl = new URL(`${environment.connect_api_endpoint_base}/bridge/catalog/search/${brandId}`);

    return this._httpClient.post<ResponseWrapper>(endpointUrl.toString(), {}, {params: {
        "public_id": this.configService.systemConfig$.publicId,
        'api_mode': apiMode
      }})
      .pipe(
        map((response: ResponseWrapper) => {
          this.logger.info("Metadata RESPONSE", response)
          return response.data as LighthouseSourceSearchResult
        })
      );
  }

  public getOrgByPublicId(publicId: string): Observable<Organization> {
    let queryParams: {[index: string]:string} = {}
    queryParams['public_id'] = publicId

    return this._httpClient.get<ResponseWrapper>(
      `${environment.connect_api_endpoint_base}/bridge/org`, {params: queryParams})
      .pipe(
        map((response: ResponseWrapper) => {
          this.logger.info("Organization", response)
          return response.data as Organization;
        })
      );
  }

  public getOrgConfig(publicId: string): Observable<Organization> {
    // Avoid cached responses created before the cookie-mode field existed.
    const params = {
      public_id: publicId,
      client_auth_contract: 'http-only-v1',
    }

    return this._httpClient.get<Organization>(
      `${environment.connect_api_endpoint_base}/bridge/config`, {params})
      .pipe(
        tap((response: Organization) => {
          this.logger.info("Organization", response)
        })
      );
  }

  public getOrgConnectionById(publicId: string, orgConnectionId: string): Observable<OrganizationConnection> {
    let queryParams: {[index: string]:string} = {}
    queryParams['public_id'] = publicId
    return this._httpClient.get<ResponseWrapper>(
      `${environment.connect_api_endpoint_base}/bridge/org_connection/${orgConnectionId}`, {params: queryParams})
      .pipe(
        map((response: ResponseWrapper) => {
          this.logger.info("Organization Connection Data", response)
          return response.data as OrganizationConnection;
        })
      );
  }

  requestHealthSystem(requestHealth: FormHealthSystemRequest): Observable<any> {
    const endpointUrl = new URL(`${environment.connect_api_endpoint_base}/support/healthsystem`);

    requestHealth.organization_id = this.configService.systemConfig$.org?.id || ""
    requestHealth.organization_name = this.configService.systemConfig$.org?.name || ""
    requestHealth.api_mode = this.configService.systemConfig$.apiMode || "test"

    return this._httpClient.post<any>(endpointUrl.toString(), requestHealth, {params: {"public_id": this.configService.systemConfig$.publicId }})
      .pipe(
        map((response: ResponseWrapper) => {
          // @ts-ignore
          return {}
        })
      );
  }

  requestSupport(request: FormSupportRequest): Observable<any> {
    const endpointUrl = new URL(`${environment.connect_api_endpoint_base}/support/request`);
    const zendeskTicket = buildSupportRequestZendeskTicket(request, {
      organizationId: this.configService.systemConfig$.org?.id || '',
      organizationName: this.configService.systemConfig$.org?.name || '',
      apiMode: this.configService.systemConfig$.apiMode || 'test',
    });

    return this._httpClient.post<any>(endpointUrl.toString(), zendeskTicket,  {params: {"public_id": this.configService.systemConfig$.publicId }})
      .pipe(
        map((response: ResponseWrapper) => {
          // @ts-ignore
          return {}
        })
      );
  }

  public storageApiUserInteractionWithPopup(): Observable<any> {
    const redirectUrl = new URL(`${window.location.origin}/consent`);

    const isDesktop = this.deviceService.isDesktop();
    let features = ""
    if(isDesktop){
      features = "popup=true,width=700,height=600"
    }

    const openedWindow = window.open(redirectUrl.toString(), "_blank", features);
    this.sentryContext.recordPopupOpen('storage_access', ConnectMode.Popup, openedWindow !== null)
    return this.waitForPopupNotification(openedWindow, SDKMode.None)
  }

  public verificationWithWebsocket(cspType: CspType): Observable<CallbackPayload> {
    //generate a unique roomId for this connection
    const roomId = uuidv4()

    //start a websocket connection to listen for the org connection completion
    const websocketUrl = this.generateWebsocketURL(roomId);

    const redirectUrlParts = new URL(`${environment.connect_api_endpoint_base}/bridge/identity_verification/connect`);
    redirectUrlParts.searchParams.set("public_id", this.configService.systemConfig$.publicId);
    redirectUrlParts.searchParams.set("csp_type", cspType || CspType.ClearCsp);
    redirectUrlParts.searchParams.set("connect_mode", ConnectMode.Websocket)
    redirectUrlParts.searchParams.set("room_id", roomId);

    this.logger.debug(redirectUrlParts.toString());
    const openedWindow = this.openWindowInPopupWithAuthHandoff(redirectUrlParts, VaultAuthHandoffPurpose.IdentityVerification);
    this.sentryContext.recordPopupOpen('identity_verification', ConnectMode.Websocket, openedWindow !== null)

    //we should start this websocket connection and wait for org connection started
    const callback = this.waitForWebsocketNotification(websocketUrl, openedWindow)
    if (openedWindow === null) {
      return callback
    }
    const callbackAttempt = this.sentryContext.recordCallbackStarted('identity_verification', ConnectMode.Websocket)
    return this.monitorCallback(
      'identity_verification',
      callbackAttempt,
      callback,
    )
      .pipe(
        switchMap((payload: CallbackPayload) => from(this.refreshAuthCookie()).pipe(map(() => payload))),
        //TODO: this is a flaky way to handle the issue where the websocket response is sent before the cookie is set in the browser
        // wait 2 seconds here -- sometimes the websocket sends the response before the cookie has been recieved by the browser (in the modal popup)
        delay(2500)
      )
  }

  public verificationWithPopup(cspType: CspType): Observable<CallbackPayload> {
    const redirectUrlParts = new URL(`${environment.connect_api_endpoint_base}/bridge/identity_verification/connect`);
    redirectUrlParts.searchParams.set("public_id", this.configService.systemConfig$.publicId);
    redirectUrlParts.searchParams.set("csp_type", cspType || CspType.ClearCsp);
    redirectUrlParts.searchParams.set("connect_mode", ConnectMode.Popup)

    const openedWindow = this.openWindowInPopupWithAuthHandoff(redirectUrlParts, VaultAuthHandoffPurpose.IdentityVerification)
    this.sentryContext.recordPopupOpen('identity_verification', ConnectMode.Popup, openedWindow !== null)

    const callback = this.waitForPopupNotification(openedWindow)
    if (openedWindow === null) {
      return callback
    }
    const callbackAttempt = this.sentryContext.recordCallbackStarted('identity_verification', ConnectMode.Popup)
    return this.monitorCallback(
      'identity_verification',
      callbackAttempt,
      callback,
    ).pipe(
      switchMap((payload: CallbackPayload) => from(this.refreshAuthCookie()).pipe(map(() => payload)))
    )
  }

  public accountConnectWithWebsocket(connectData: ConnectPayload): Observable<CallbackPayload> {
    //generate a unique roomId for this connection
    const roomId = uuidv4()

    //start a websocket connection to listen for the org connection completion
    const websocketUrl = this.generateWebsocketURL(roomId);

    const redirectUrlParts = this.generateConnectURL(connectData);
    redirectUrlParts.searchParams.set("connect_mode", ConnectMode.Websocket)
    redirectUrlParts.searchParams.set("room_id", roomId);
    this.logger.debug(redirectUrlParts.toString());
    const openedWindow = connectData.vault_profile_connection_id
      ? this.openWindowInPopupWithAuthHandoff(redirectUrlParts, VaultAuthHandoffPurpose.BridgeConnect)
      : this.openWindowInPopup(redirectUrlParts);
    this.sentryContext.recordPopupOpen('connection', ConnectMode.Websocket, openedWindow !== null)

    //we should start this websocket connection and wait for org connection started
    const callback = this.waitForWebsocketNotification(websocketUrl, openedWindow)
    if (openedWindow === null) {
      return callback
    }
    const callbackAttempt = this.sentryContext.recordCallbackStarted('connection', ConnectMode.Websocket)
    return this.monitorCallback('connection', callbackAttempt, callback)
  }

  public accountConnectWithPopup(connectData: ConnectPayload): Observable<CallbackPayload> {
    const redirectUrlParts = this.generateConnectURL(connectData);
    redirectUrlParts.searchParams.set("connect_mode", ConnectMode.Popup)
    this.logger.debug(redirectUrlParts.toString());

    const openedWindow = connectData.vault_profile_connection_id
      ? this.openWindowInPopupWithAuthHandoff(redirectUrlParts, VaultAuthHandoffPurpose.BridgeConnect)
      : this.openWindowInPopup(redirectUrlParts);
    this.sentryContext.recordPopupOpen('connection', ConnectMode.Popup, openedWindow !== null)
    const callback = this.waitForPopupNotification(openedWindow)
    if (openedWindow === null) {
      return callback
    }
    const callbackAttempt = this.sentryContext.recordCallbackStarted('connection', ConnectMode.Popup)
    return this.monitorCallback('connection', callbackAttempt, callback)
  }

   public authorizeTefcaDirect(vaultConnectionIds: string[], external_id?: string): Observable<any> {
    const url = `${environment.connect_api_endpoint_base}/bridge/vault_connection/authorize`;
    return this._httpClient.post<ResponseWrapper>(
      url,
      {
        vault_connection_ids: vaultConnectionIds,
        external_id: external_id
      },
      { params: { public_id: this.configService.systemConfig$.publicId } }
    ).pipe(map((resp: ResponseWrapper) => resp.data));
  }

  //this function only works in ApiMode.Test. It will allow us to "reset" the enabled vault profile connections for a patient + org combo
  public revokeVaultProfileConnections(vaultProfileConnectionIds?: string[]): Observable<any> {
    const url = `${environment.connect_api_endpoint_base}/bridge/vault_connection/revoke`;
    return this._httpClient.post<ResponseWrapper>(
      url,
      {
        vault_connection_ids: vaultProfileConnectionIds,
      },
      { params: { public_id: this.configService.systemConfig$.publicId } }
    ).pipe(map((resp: ResponseWrapper) => resp.data));
  }


  public async refreshAuthCookie(): Promise<unknown> {
    const response = await firstValueFrom(this._httpClient.get(
      `${environment.connect_api_endpoint_base}/bridge/vault_auth_refresh`, {
        withCredentials: true,
        params: {public_id: this.configService.systemConfig$.publicId},
      },
    ))
    // Refresh changes the cookie, so the cached session is stale.
    this.authService.ClearSession()
    return response
  }

  /// HELPERS

  public openWindowInPopup(redirectUrlParts: URL): Window | null {
    const isDesktop = this.deviceService.isDesktop();
    let features = ""
    if(isDesktop){
      //add options for window.open, popup only for desktop (mobile and table will open a new tab)
      features = "popup=true,width=700,height=600"
    }

    //We're always in popup mode, we can open a new window, rather than redirecting the current window (which is an app frame)
    //open a external url in a new window
    return window.open(redirectUrlParts.toString(), "_blank", features);
  }

  // Native SDK WebViews need the final URL in window.open; browser embeds use a scoped-token POST
  // so the popup does not depend on access to the iframe's partitioned cookie.
  public openWindowInPopupWithAuthHandoff(redirectUrlParts: URL, purpose: VaultAuthHandoffPurpose): Window | null {
    this.logger.log("Opening popup with auth handoff: ", redirectUrlParts, purpose);
    if (isNativeSdkMode(this.configService.systemConfig$.sdkMode)) {
      return this.openWindowInPopup(redirectUrlParts)
    }

    const isDesktop = this.deviceService.isDesktop();
    let features = ""
    if(isDesktop){
      //add options for window.open, popup only for desktop (mobile and table will open a new tab)
      features = "popup=true,width=700,height=600"
    }

    const target = `VaultAuthHandoffPopupWindow-${uuidv4()}`;
    const opened = window.open('', target, features);
    if (!opened) {
      return null
    }

    this.authService.GetVaultAuthHandoffToken(purpose)
      .then((handoffToken) => {
        const form = document.createElement("form");
        form.setAttribute("method", "post");
        form.setAttribute("action", redirectUrlParts.toString());
        form.setAttribute("target", target);
        form.style.display = "none";

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = FASTEN_AUTH_VAULT_COOKIE_NAME;
        input.value = handoffToken;
        form.appendChild(input);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      })
      .catch((error) => {
        this.logger.error("failed to fetch vault auth handoff token", {purpose, error});
        opened.close();
      });

    return opened
  }

  public generateWebsocketURL(roomId: string): URL {
    const websocketUrlParts = new URL(`wss://websocket.${environment.connect_base_domain}/v1`);
    const websocketParams = new URLSearchParams()
    websocketParams.set("public_id", this.configService.systemConfig$.publicId)
    websocketParams.set("room_id", roomId)
    websocketUrlParts.search = websocketParams.toString()
    return websocketUrlParts;
  }

  public generateConnectURL(connectData: ConnectPayload): URL {
    const redirectUrlParts = new URL(`${environment.connect_api_endpoint_base}/bridge/connect`);
    const redirectParams = new URLSearchParams()
    redirectParams.set("public_id", this.configService.systemConfig$.publicId)
    redirectParams.set("brand_id", connectData.brand_id)
    redirectParams.set("portal_id", connectData.portal_id)
    redirectParams.set("endpoint_id", connectData.endpoint_id)
    redirectParams.set("sdk_mode", this.configService.systemConfig$.sdkMode as string)


    if (connectData.org_connection_id) { //reconnectOrgConnectionId
      redirectParams.set("reconnect_org_connection_id", connectData.org_connection_id)
    }
    if (connectData.external_id) {
      redirectParams.set("external_id", connectData.external_id)
    }
    if (connectData.external_state) {
      redirectParams.set("external_state", connectData.external_state)
    }
    if (connectData.vault_profile_connection_id) { //reconnectVaultProfileConnectionId
      redirectParams.set("reconnect_vault_profile_connection_id", connectData.vault_profile_connection_id)
    }
    redirectUrlParts.search = redirectParams.toString()
    return redirectUrlParts;
  }

  protected waitForPopupNotification(openedWindow: Window | null, overrideSdkMode?: SDKMode): Observable<CallbackPayload> {
    const sdkMode = overrideSdkMode ?? this.configService.systemConfig$.sdkMode;
    return waitForPostMessageOrgConnectionOrTimeout(this.logger, openedWindow!, sdkMode);
  }

  protected waitForWebsocketNotification(websocketUrl: URL, openedWindow: Window | null, overrideSdkMode?: SDKMode): Observable<CallbackPayload> {
    const sdkMode = overrideSdkMode ?? this.configService.systemConfig$.sdkMode;
    return waitForWebsocketOrgConnectionOrTimeout(this.logger, websocketUrl, openedWindow!, sdkMode);
  }

  private monitorCallback(
    flow: CallbackFlow,
    attempt: CallbackAttempt,
    callback: Observable<CallbackPayload>,
  ): Observable<CallbackPayload> {
    return callback.pipe(tap({
      next: () => this.sentryContext.recordCallbackReceived(flow, attempt),
      error: (error) => this.sentryContext.recordCallbackError(flow, attempt, error),
    }))
  }

  public reverseGeocodePostalCode(latitude: number, longitude: number): Observable<GeocodeResponse> {
    let queryParams: {[index: string]:string} = {}
    queryParams['public_id'] = this.configService.systemConfig$.publicId
    queryParams['latlng'] = `${latitude},${longitude}`
    const url = `${environment.connect_api_endpoint_base}/bridge/catalog/geocode`;
    return this._httpClient.get<ResponseWrapper>(url, {params: queryParams})
      .pipe(map((resp: ResponseWrapper) => resp.data as GeocodeResponse));

  }

}
